import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { STUDIO_INQUIRY_PRODUCTION_COMPANY } from '../../constants/common';
import { FormValidationService } from '../../services/form-validations/form-validation.service';

interface CountryOption {
  countryId: string;
  countryName: string;
  states?: { stateId: string; stateName: string }[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'app-studio-inquiry-production-company',
    templateUrl: './studio-inquiry-production-company.component.html',
    styleUrls: ['./studio-inquiry-production-company.component.scss'],
    standalone: false
})
export class StudioInquiryProductionCompanyComponent implements OnInit {
  /** Everything captured on the earlier steps of the inquiry */
  @Input() projectInformation: any;
  /**
   * Country list in the same shape as the company metadata endpoint
   * (`countryDetails`), each country carrying its own nested states.
   */
  @Input() countries: CountryOption[] = [];
  /** Values captured earlier, so stepping back and forward does not lose them */
  @Input() productionCompany: any;
  @Output() productionCompanySubmitted = new EventEmitter<any>();
  constants = STUDIO_INQUIRY_PRODUCTION_COMPANY;
  companyForm!: FormGroup;
  states: { stateId: string; stateName: string }[] = [];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formValidationService: FormValidationService,
    public activeModal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    this.companyForm = this.formBuilder.group({
      companyName: ['', [Validators.required, Validators.maxLength(50)]],
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.required, this.formValidationService.validateEmail]],
      jobTitle: ['', [Validators.required, Validators.maxLength(50)]],
      phone: ['', [Validators.required, this.formValidationService.validateTrPhoneNo]],
      country: [''],
      street: ['', Validators.maxLength(85)],
      city: ['', Validators.maxLength(50)],
      state: [''],
      zipCode: [''],
      addressPhone: [''],
      billingDifferent: [false],
      preferredContact: [''],
    });
    if (this.productionCompany) {
      this.companyForm.patchValue(this.productionCompany);
    }
    this.setStates(this.companyForm.get('country')?.value);
  }

  /**
   * onCountryChange() - Swaps the state list to the ones belonging to the country
   * @param event the country select change event
   */
  onCountryChange(event: Event): void {
    const countryId = (event?.target as HTMLSelectElement)?.value;
    this.setStates(countryId);
    this.companyForm.get('state')?.setValue('');
  }

  /**
   * setStates() - Resolves the nested state list for the given country
   * @param countryId the selected country's id
   */
  setStates(countryId: string): void {
    this.states = this.countries?.find((country) => country.countryId === countryId)?.states || [];
  }

  /**
   * onNext() - Emits the production company details and closes the modal
   */
  onNext(): void {
    const productionCompany = { ...this.companyForm.value };
    this.productionCompanySubmitted.emit(productionCompany);
    this.activeModal.close(productionCompany);
  }

  /**
   * onBack() - Returns to the project information step
   */
  onBack(): void {
    this.activeModal.dismiss('back');
  }

  /**
   * closeModal() - Dismisses the inquiry
   */
  closeModal(): void {
    this.activeModal.dismiss();
  }
}
